/**
 * session-bridge.ts
 * Bridge module: reads GameArcadeSession (loaded via <script> tag from ../session.js)
 * and manages the virtual money system for Tiến lên miền Nam.
 */

/* ── Type declaration for the external global ── */
declare global {
  interface Window {
    GameArcadeSession?: {
      getPlayer(): ArcadePlayer | null;
      ensurePlayer(opts?: { prompt?: boolean; force?: boolean; defaultName?: string }): Promise<ArcadePlayer | null>;
      submitScore(game: string, score: number): Promise<unknown>;
      fetchPlayerStats(opts?: { playerId?: number; name?: string }): Promise<PlayerStatsResponse>;
      getApiBase?(): string;
      changePlayer(): Promise<ArcadePlayer | null>;
    };
  }
}

export interface ArcadePlayer {
  playerId: number;
  name: string;
  created?: boolean;
}

interface PlayerScore {
  game: string;
  bestScore?: number;
  lastScore?: number;
}

interface PlayerStatsResponse {
  player: ArcadePlayer | null;
  scores: PlayerScore[];
}

/* ── Constants ── */
export const STARTING_BALANCE = 10_000_000; // 10 triệu ₫
export const DEFAULT_BET_AMOUNT = 500_000;
export const MIN_BET_AMOUNT = 1_000;
export const MAX_BET_AMOUNT = STARTING_BALANCE;
const BALANCE_KEY = (id: number) => `tienlen_balance_${id}`;
const BANK_ACCOUNT_KEY = (id: number) => `tienlen_bank_account_${id}`;
const GAME_KEY = 'tien-len';
const DEFAULT_API_BASE = 'https://game-arcade-api.dinhvanhai07071997.workers.dev';

/* ── Session helpers ── */
export function getArcadePlayer(): ArcadePlayer | null {
  return window.GameArcadeSession?.getPlayer() ?? null;
}

export async function ensureArcadePlayer(defaultName?: string): Promise<ArcadePlayer | null> {
  if (!window.GameArcadeSession) return null;
  return (window.GameArcadeSession.ensurePlayer({ defaultName }) as Promise<ArcadePlayer | null>);
}

/* ── Balance helpers ── */
export function getBalance(playerId: number): number {
  return getBankAccount(playerId);
}

export async function loadBankAccount(player: ArcadePlayer): Promise<number> {
  const remote = await fetchCloudBankAccount(player);
  if (typeof remote === 'number') {
    cacheBankAccount(player.playerId, remote);
    return remote;
  }
  if (remote === null) {
    saveBankAccount(player.playerId, STARTING_BALANCE);
    return STARTING_BALANCE;
  }

  const cached = readCachedBankAccount(player.playerId);
  if (cached !== null) return cached;

  saveBankAccount(player.playerId, STARTING_BALANCE);
  return STARTING_BALANCE;
}

export function getBankAccount(playerId: number): number {
  return readCachedBankAccount(playerId) ?? STARTING_BALANCE;
}

export function saveBalance(playerId: number, amount: number): void {
  saveBankAccount(playerId, amount);
}

export function saveBankAccount(playerId: number, amount: number): void {
  const clamped = Math.max(0, Math.round(amount));
  cacheBankAccount(playerId, clamped);
  syncBankAccountScore(clamped);
}

function cacheBankAccount(playerId: number, amount: number): void {
  const clamped = Math.max(0, Math.round(amount));
  localStorage.setItem(BANK_ACCOUNT_KEY(playerId), String(clamped));
  localStorage.setItem(BALANCE_KEY(playerId), String(clamped));
}

function readCachedBankAccount(playerId: number): number | null {
  const bankRaw = localStorage.getItem(BANK_ACCOUNT_KEY(playerId));
  if (bankRaw !== null) {
    const bank = parseInt(bankRaw, 10);
    return isNaN(bank) ? null : bank;
  }

  const legacyRaw = localStorage.getItem(BALANCE_KEY(playerId));
  if (legacyRaw !== null) {
    const legacy = parseInt(legacyRaw, 10);
    if (!isNaN(legacy)) {
      cacheBankAccount(playerId, legacy);
      return legacy;
    }
  }

  return null;
}

async function fetchCloudBankAccount(player: ArcadePlayer): Promise<number | null | undefined> {
  const apiBase = window.GameArcadeSession?.getApiBase?.() || DEFAULT_API_BASE;
  const url = new URL('/api/player', apiBase.replace(/\/+$/, '') + '/');
  url.searchParams.set('playerId', String(player.playerId));

  try {
    const response = await fetch(url.toString());
    if (!response.ok) return undefined;

    const stats = (await response.json()) as PlayerStatsResponse;
    const score = stats?.scores?.find(item => item.game === GAME_KEY);
    return typeof score?.bestScore === 'number'
      ? Math.max(0, Math.round(score.bestScore))
      : null;
  } catch (error) {
    console.warn('Không thể tải Bank Account từ Cloudflare, dùng cache tạm:', error);
    return undefined;
  }
}

export function syncBankAccountScore(amount: number): void {
  const score = Math.max(0, Math.round(amount));
  void window.GameArcadeSession?.submitScore(GAME_KEY, score);
}

/* ── Money calculation per round ── */
export interface MoneyDelta {
  delta: number;
  newBalance: number;
  lines: string[];   // human-readable breakdown lines
}

export interface MoneyPenaltyLine {
  playerName?: string;
  congPenalty: number;
  rottenItems: string[];
}

export function calcAndApplyRoundMoney(opts: {
  playerId: number;
  betAmount: number;
  isWinner: boolean;
  isInstantWin: boolean;
  scoreLines?: MoneyPenaltyLine[];
  congPenalty: number;
  rottenPenalty: number;
  rottenItems: string[];
}): MoneyDelta {
  const current = getBalance(opts.playerId);
  const betAmount = clampBetAmount(opts.betAmount);
  let delta = 0;
  const lines: string[] = [];

  if (opts.isWinner) {
    const winnerGain = (opts.scoreLines || []).reduce(
      (sum, line) => sum + calcPenaltyAmount(betAmount, line),
      0
    );
    delta += winnerGain;
    lines.push(`🥇 Thu từ người thua ${fmtDelta(winnerGain)}`);
    if (opts.isInstantWin) {
      lines.push('⭐ Tới trắng: không cộng riêng, thu đúng tổng tiền phạt');
    }
    (opts.scoreLines || []).forEach((line) => {
      const amount = calcPenaltyAmount(betAmount, line);
      lines.push(`${line.playerName || 'Người thua'} trả ${fmtMoney(amount)}`);
    });
  } else {
    delta -= betAmount;
    lines.push(`💸 Thua ${fmtDelta(-betAmount)}`);
    if (opts.congPenalty > 0) {
      const congFine = betAmount * 2;
      delta -= congFine;
      lines.push(`😱 Cóng ${fmtDelta(-congFine)}`);
    }
    if (opts.rottenItems.length > 0) {
      const rottenFine = opts.rottenItems.length * betAmount;
      delta -= rottenFine;
      lines.push(`🤢 Thối hàng ${fmtDelta(-rottenFine)} (${opts.rottenItems.join(', ')})`);
    }
  }

  const newBalance = Math.max(0, current + delta);
  saveBalance(opts.playerId, newBalance);
  return { delta, newBalance, lines };
}

function calcPenaltyAmount(betAmount: number, line: MoneyPenaltyLine): number {
  let total = betAmount;
  if (line.congPenalty > 0) total += betAmount * 2;
  total += line.rottenItems.length * betAmount;
  return total;
}

export function clampBetAmount(value: number): number {
  const amount = Math.round(Number(value) || DEFAULT_BET_AMOUNT);
  return Math.min(MAX_BET_AMOUNT, Math.max(MIN_BET_AMOUNT, amount));
}

/* ── Number formatting ── */
export function fmtMoney(n: number): string {
  return `${Math.round(n).toLocaleString('vi-VN')} VND`;
}

export function fmtDelta(n: number): string {
  const prefix = n > 0 ? '+' : n < 0 ? '-' : '';
  return prefix + fmtMoney(Math.abs(n));
}
